import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import {
  checkNotificationPermission,
  requestNotificationPermission,
  setupLivestreamNotifications,
} from '../utils/notifications';
import { publicApi } from '../utils/publicApi';
import playButtonSvg from '../assets/layer_1.svg';

/** YouTube iframe: reduce branding, related videos, captions default, annotations (UI still partly controlled by YouTube). */
function buildYoutubeEmbedUrl(videoId, { autoplay = false } = {}) {
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    rel: '0',
    modestbranding: '1',
    cc_load_policy: '0',
    iv_load_policy: '3',
    playsinline: '1',
    fs: '1',
    controls: '1',
  });
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

function getYoutubeVideoId(url) {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
}

const LiveStream = (props) => {
  const [streamData, setStreamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [embeddedPlaying, setEmbeddedPlaying] = useState(false);
  const [thumbTier, setThumbTier] = useState(0);
  const { t } = useLanguage();

  useEffect(() => {
    fetchStreamData();
    // Set visible after component mounts to trigger animations
    setIsVisible(true);
    
    // Setup notifications regardless of UI
    const setupNotifications = async () => {
      // Request permission if needed
      if (!checkNotificationPermission()) {
        await requestNotificationPermission();
      }
      
      // Setup notifications
      setupLivestreamNotifications();
    };
    
    setupNotifications();
  }, []);

  useEffect(() => {
    if (!props.embedded || !streamData?.youtube_url) return;
    const id = getYoutubeVideoId(streamData.youtube_url);
    if (!id) return;
    setEmbeddedPlaying(false);
    setThumbTier(0);
  }, [props.embedded, streamData?.youtube_url]);

  const fetchStreamData = async () => {
    try {
      const data = await publicApi.getLivestream();
      setStreamData(data);
    } catch (error) {
      console.error('Error fetching stream data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  // If no stream data or no valid URL, don't render anything
  if (!streamData || !streamData.youtube_url) {
    return null;
  }

  const videoId = getYoutubeVideoId(streamData.youtube_url);

  // If no valid video ID, don't render anything
  if (!videoId) {
    return null;
  }

  const thumbUrls = [
    `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
  ];

  // Page entrance animation
  const pageEntrance = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.8, 
        ease: "easeInOut",
        when: "beforeChildren"
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  
  // Animation for the video container
  const videoContainerAnimation = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: {
        type: "spring",
        stiffness: 70,
        damping: 15,
        delay: 0.8
      }
    }
  };

  // Embedded mode (e.g. About main highlights): thumbnail + custom play; iframe only after click — URL stays dynamic from API.
  if (props.embedded) {
    return (
      <div className="relative h-full w-full min-h-[200px]">
        {!embeddedPlaying ? (
          <>
            <img
              src={thumbUrls[thumbTier]}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              onError={() => setThumbTier((tier) => Math.min(tier + 1, thumbUrls.length - 1))}
            />
            <div
              className="absolute inset-0 bg-black/25"
              aria-hidden
            />
            <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
              <button
                type="button"
                onClick={() => setEmbeddedPlaying(true)}
                className="group flex h-14 min-h-[44px] min-w-[44px] w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm shadow-md ring-1 ring-white/35 transition-all duration-200 hover:bg-[#422A7E] hover:backdrop-blur-none hover:shadow-lg hover:ring-black/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#422A7E]"
                aria-label={t('website.livestream.playVideo')}
              >
                <img
                  src={playButtonSvg}
                  alt=""
                  className="pointer-events-none h-[39px] w-[31px] transition-[filter] duration-200 group-hover:brightness-0 group-hover:invert"
                  width={31}
                  height={39}
                />
              </button>
            </div>
          </>
        ) : (
          <iframe
            src={buildYoutubeEmbedUrl(videoId, { autoplay: true })}
            title={t('website.livestream.watchLive')}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        )}
      </div>
    );
  }

  return (
    <AnimatePresence>
      <motion.section 
        id="livestream" 
        className="py-24 bg-gradient-to-br from-slate-50 via-secondary/20 to-primary/10 relative overflow-hidden"
        initial="hidden"
        animate={isVisible ? "visible" : "hidden"}
        variants={pageEntrance}
      >
        {/* Static background elements - no animations for better performance */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-40 w-80 h-80 bg-primary/8 rounded-full blur-[80px]"></div>
          <div className="absolute top-0 -right-40 w-80 h-80 bg-secondary/15 rounded-full blur-[80px]"></div>
          <div className="absolute -bottom-40 left-20 w-80 h-80 bg-primary/8 rounded-full blur-[80px]"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          {/* Standardized Title Section */}
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-100 shadow-sm text-primary text-xs font-bold tracking-widest uppercase mb-5">
                {t('website.livestream.title')}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                {t('website.livestream.watchLive')}
              </h2>
              <p className="text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
                {t('website.livestream.subtitle')}
              </p>
            </motion.div>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.div 
              variants={videoContainerAnimation}
              className="relative"
            >
              <motion.div 
                className="absolute -inset-1 bg-gradient-to-r from-primary to-secondary rounded-2xl transform rotate-1 opacity-70"
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ opacity: 0.7, rotate: 1 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              ></motion.div>
              <motion.div 
                className="relative overflow-hidden rounded-2xl shadow-2xl"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 70,
                  damping: 15,
                  delay: 1.3
                }}
              >
                <div className="aspect-w-16 aspect-h-9">
                  <iframe
                    src={buildYoutubeEmbedUrl(videoId, { autoplay: true })}
                    title={t('website.livestream.watchLive')}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

export default LiveStream;